import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import urlParser from 'js-video-url-parser'

import { HOST_NAME } from '@data/config/url'
import { getStaticImageUrl } from '@data/config/imageUrl'
import { BACKGROUNDS as BACKGROUND_PATHS } from '@data/config/images'

import { XStack, YStack } from 'tamagui'

import { Link } from '../Link'
import { Text, H3 } from '../Text'

import { Card } from '../Card'

import { Image } from '../Image'

import { UserPrimaryPhoto, UserDefaultPhoto } from '../User/UserPrimaryPhoto'
// import { UserItem } from '../User/UserItem'
import { FormattedMarkdown } from '../FormattedMarkdown/FormattedMarkdown'

import {
  PresentationItemNew_presentation$data,
  PresentationItemNew_presentation$key,
} from './__generated__/PresentationItemNew_presentation.graphql'

// Generate full URLs for backgrounds
const BACKGROUNDS = BACKGROUND_PATHS.map((path) => getStaticImageUrl(path))

const ALLOWED_ELEMENTS = ['a', 'p']

export const PresentationItem = Link.styleable<{
  presentation: PresentationItemNew_presentation$key
  titleNumberOfLines?: number
  numberOfLines?: number
  showCoverPhoto?: boolean
  href?: string
}>(
  (
    {
      presentation,
      titleNumberOfLines,
      numberOfLines = 10,
      showCoverPhoto = true,
      ...props
    },
    ref
  ) => {
    const data = useFragment(
      graphql`
        fragment PresentationItemNew_presentation on Presentation {
          rowId
          description
          prettyUrl
          title
          updatedAt

          videoSourceUrl

          presenterFirstName
          presenterLastName

          presenter {
            firstName
            lastName
            ...UserPrimaryPhotoNew_user
          }

          # Signed URL for guest presenter avatar (when no linked User)
          guestPresenterAvatarUrl(transform: { w: 32, h: 32, dpr: 3, fo: "auto" })
        }
      `,
      presentation
    )

    return (
      <Link
        {...props}
        ref={ref}
        href={`/presentations/${data.prettyUrl}`}
        aria-label={`Link to ${data.title} Presentation`}
        display='flex'
        flexDirection='column'
        hoverStyle={{ textDecorationLine: 'none' }}
      >
        {showCoverPhoto && <PresentationCoverPhoto presentation={data} />}

        <Card
          gap='$4'
          padding='$4'
          borderRadius='$8'
          borderTopLeftRadius={0}
          borderTopRightRadius={0}
          flex={props.flex}
        >
          <H3 size='$6' numberOfLines={titleNumberOfLines}>
            {data.title}
          </H3>

          <YStack flex={props.flex}>
            <FormattedMarkdown
              allowedElements={ALLOWED_ELEMENTS}
              formattedVariant='PLAIN_TEXT'
              numberOfLines={numberOfLines}
              textSize='$4'
            >
              {data.description}
            </FormattedMarkdown>
          </YStack>

          {data.presenter ? (
            <XStack alignItems='center' gap='$3'>
              <UserPrimaryPhoto user={data.presenter} size='$8' />

              <Text size='$4' numberOfLines={1}>
                {data.presenter.firstName} {data.presenter.lastName}
              </Text>
            </XStack>
          ) : (
            <XStack alignItems='center' gap='$3'>
              <UserDefaultPhoto
                defaultAvatarUrl={data.guestPresenterAvatarUrl}
                width={32}
                height={32}
                borderRadius={16}
              />

              <Text size='$4' numberOfLines={1}>
                {data.presenterFirstName} {data.presenterLastName}
              </Text>
            </XStack>
          )}
        </Card>
      </Link>
    )
  }
)

const PresentationCoverPhoto = ({
  presentation,
}: {
  presentation: PresentationItemNew_presentation$data
}) => {
  const videoThumbnailURL = useMemo(() => {
    if (presentation.videoSourceUrl) {
      const videoInfo = urlParser.parse(presentation.videoSourceUrl)

      if (videoInfo?.provider === 'youtube') {
        const photoUrl = urlParser.create({
          format: 'longImage',
          params: { imageQuality: 'maxresdefault' },
          videoInfo,
        })

        if (photoUrl) {
          return photoUrl
        }
      }
    }

    return null
  }, [presentation.videoSourceUrl])

  const socialCardURL = useMemo(() => {
    return `${HOST_NAME}/social/presentation/${presentation.prettyUrl}/card.svg?${presentation.updatedAt}`
  }, [presentation.prettyUrl, presentation.updatedAt])

  return (
    <Image
      src={videoThumbnailURL || socialCardURL}
      style={{
        width: '100%',
        maxHeight: 280,
        borderTopLeftRadius: 32,
        borderTopRightRadius: 32,
        aspectRatio: '2',
      }}
    />
  )
}
